import {
  FERTILIZER_COMPONENTS,
  FERTILIZER_METHODS,
  FERTILIZER_TYPE,
  FERTILIZER_TYPE_UAN,
  FERTILIZER_UNIT,
  FertilizerComponents,
  FertilizerType,
  FertilizerUnit,
  RDFertilizerEvent,
  UNITS_BY_TYPE,
} from '@cibo/core'
import { CheckboxField, DatePickerField } from '@cibo/ui'
import {
  Box,
  Button,
  Collapse,
  FormControl,
  FormLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import { Field, Formik, FormikHelpers, useField } from 'formik'
import { Select, TextField } from 'formik-mui'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useIsRequired } from '../../useIsRequired'
import { useShowDetailAttribute } from '../../useShowDetailAttribute'
import { FertilizerEventValidationSchema } from '../Fertilizer'
import { FertilizerEventEditorProps } from './FertilizerEventTypes'

// @TODO: split this component into smaller components
// eslint-disable-next-line complexity
export const FertilizerEventEditor = ({
  expanded,
  filterInputs,
  name,
  onPressRemove,
  requirement,
  setExpanded,
  shouldDisableDate,
  onEditEvent,
  year,
}: FertilizerEventEditorProps) => {
  const { t } = useTranslation('@cibo/landmanager/FertilizerEditor')
  const [{ value }, field, { setValue, setTouched }] = useField<RDFertilizerEvent>(name)

  const { error } = useField<RDFertilizerEvent>(`${name}.combined`)[1]
  const unitsHelper = useField<string>(`${name}.units`)[2]

  const combinedError = error

  useEffect(() => {
    if (FERTILIZER_TYPE_UAN.includes(value?.type)) {
      unitsHelper.setValue('gallonsPerAcre')
    }
  }, [value?.type])

  const defaultMonth = new Date(year, 3)
  const comboFieldStyle = useMemo(
    () => ({
      zIndex: 0,
      marginBottom: 2,
      '& .MuiTextField-root': {
        flex: 1,
      },
      '& .MuiTextField-root .MuiOutlinedInput-notchedOutline': {
        borderRadius: 0,
        marginLeft: '-1px',
        zIndex: 0,
        borderColor: !!combinedError ? 'error.main' : undefined,
      },
      '& .MuiTextField-root:first-child .MuiOutlinedInput-notchedOutline': {
        borderTopLeftRadius: 4,
        borderBottomLeftRadius: 4,
      },
      '& .MuiTextField-root:last-child .MuiOutlinedInput-notchedOutline': {
        borderTopRightRadius: 4,
        borderBottomRightRadius: 4,
      },
      '& .MuiError .MuiOutlinedInput-notchedOutline': {
        zIndex: 1,
      },
    }),
    [error]
  )
  const isRequired = useIsRequired(requirement)
  const show = useShowDetailAttribute<RDFertilizerEvent>(filterInputs)

  const validationSchema = FertilizerEventValidationSchema({ t, requirement })

  const handleSubmit = (
    values: RDFertilizerEvent,
    { setSubmitting }: FormikHelpers<RDFertilizerEvent>
  ) => {
    setTouched(true, false)
    setValue(values)
    setSubmitting(false)
    onEditEvent(false)
    setExpanded(false)
  }

  return (
    <Formik
      initialValues={
        value ||
        ({ date: null, type: null, method: null, units: null } as unknown as RDFertilizerEvent)
      }
      validationSchema={validationSchema}
      validateOnMount
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {/* eslint-disable-next-line complexity */}
      {({ isValid, resetForm, submitForm, values, dirty, errors }) => {
        const fertilizerUnits = UNITS_BY_TYPE[values?.type] || FERTILIZER_UNIT

        // @ts-ignore amountPresent is a "synthetic" field to generate this error / not a form value
        const amountNotPresent = errors.amountPresent
        // @ts-ignore combinedPercent is a "synthetic" field to generate this error / not a form value
        const combinedPercentError = errors.combinedPercent

        return (
          <TableRow>
            <TableCell sx={{ paddingBlock: 0, borderBottomWidth: expanded ? 1 : 0 }} colSpan={5}>
              <Collapse in={expanded}>
                <Stack sx={{ paddingBlock: 1 }} spacing={2}>
                  {(show('type') || show('date')) && (
                    <Box>
                      <Grid spacing={2} container>
                        {show('date') && (
                          <Grid xs={12} md={6} item>
                            <FormControl fullWidth required>
                              <FormLabel>{t('date')}</FormLabel>
                              <DatePickerField
                                name={`date`}
                                minDate={new Date(year - 1, 6, 1)}
                                maxDate={new Date(year + 1, 0, 1)}
                                defaultCalendarMonth={defaultMonth}
                                shouldDisableDate={shouldDisableDate}
                                InputProps={{
                                  //@ts-ignore this will be passed to the input element, but is not in the type definition
                                  'data-testid': `${name}.date`,
                                }}
                              />
                            </FormControl>
                          </Grid>
                        )}

                        {show('type') && (
                          <Grid xs={12} md={6} item>
                            <FormControl
                              fullWidth
                              // type is required if unit is required, because units input relies on type value
                              required={isRequired('type') || isRequired('units')}
                            >
                              <FormLabel>{t('type')}</FormLabel>
                              <Field
                                component={Select}
                                id={`${name}.type`}
                                name={`type`}
                                data-testid={`${name}.type`}
                              >
                                {FERTILIZER_TYPE.map((type: FertilizerType) => (
                                  <MenuItem key={type} value={type}>
                                    {t(type)}
                                  </MenuItem>
                                ))}
                              </Field>
                            </FormControl>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  )}

                  {(show('rate') || show('units') || show('method')) && (
                    <Box>
                      <Grid spacing={2} container>
                        {show('method') && (
                          <Grid xs={12} md={4} item>
                            <FormControl required={isRequired('method')} fullWidth>
                              <FormLabel>{t('method')}</FormLabel>
                              <Field
                                component={Select}
                                id={`${name}.method`}
                                name={`method`}
                                data-testid={`${name}.method`}
                              >
                                {FERTILIZER_METHODS.map(type => (
                                  <MenuItem key={type} value={type}>
                                    {t(type)}
                                  </MenuItem>
                                ))}
                              </Field>
                            </FormControl>
                          </Grid>
                        )}

                        {show('rate') && (
                          <Grid xs={12} md={4} item>
                            <FormControl fullWidth>
                              <FormLabel>{t('rate')}</FormLabel>
                              <Field
                                component={TextField}
                                name={`rate`}
                                InputProps={{
                                  type: 'number',
                                  inputProps: {
                                    min: 0,
                                  },
                                }}
                                data-testid={`${name}.rate`}
                              />
                            </FormControl>
                          </Grid>
                        )}

                        {show('units') && (
                          <Grid xs={12} md={4} item>
                            <FormControl required={isRequired('units')} fullWidth>
                              <FormLabel htmlFor={`${name}.units`}>{t('unit')}</FormLabel>
                              <Field
                                component={Select}
                                name={`units`}
                                disabled={'' === values?.type || fertilizerUnits.length === 1}
                                data-testid={`${name}.units`}
                              >
                                {fertilizerUnits.map((type: FertilizerUnit) => (
                                  <MenuItem key={type} value={type}>
                                    {t(type)}
                                  </MenuItem>
                                ))}
                              </Field>
                            </FormControl>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  )}

                  {(show('nitrogenAmnt') || show('product')) && (
                    <Box>
                      <Grid spacing={2} container>
                        {show('nitrogenAmnt') && (
                          <Grid xs={12} md={4} item>
                            <FormControl required={isRequired('nitrogenAmnt')}>
                              <FormLabel>{t('nitrogenAmnt')}</FormLabel>
                              <Field
                                component={TextField}
                                name={`nitrogenAmnt`}
                                InputProps={{
                                  type: 'number',
                                  inputProps: {
                                    min: 0,
                                    max: 400,
                                  },
                                  endAdornment: (
                                    <InputAdornment position="end">{t('lbs/ac')}</InputAdornment>
                                  ),
                                }}
                                data-testid={`${name}.nitrogenAmnt`}
                              />
                            </FormControl>
                          </Grid>
                        )}
                        {show('product') && (
                          <Grid xs={12} md={8} item>
                            <FormControl fullWidth>
                              <FormLabel>{t('product')}</FormLabel>
                              <Field
                                component={TextField}
                                name={`product`}
                                data-testid={`${name}.product`}
                              />
                            </FormControl>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  )}

                  <Typography variant="caption">
                    {t('amountNotPresent')}{' '}
                    {!amountNotPresent ? (
                      <Typography display="inline" color="success">
                        ✓
                      </Typography>
                    ) : (
                      <Typography display="inline" color="error">
                        x
                      </Typography>
                    )}
                  </Typography>

                  {(show('slowRelease') || show('inhibitor') || show('ammonium')) && (
                    <Stack spacing={0}>
                      {show('slowRelease') && (
                        <Stack direction="row" alignItems="center" flex={1}>
                          <CheckboxField name={`slowRelease`} data-testid={`${name}.slowRelease`} />
                          <InputLabel
                            htmlFor={`${name}.slowRelease`}
                            required={isRequired('slowRelease')}
                          >
                            {t('slowRelease')}
                          </InputLabel>
                        </Stack>
                      )}
                      {show('inhibitor') && (
                        <Stack direction="row" alignItems="center">
                          <CheckboxField name={`inhibitor`} data-testid={`${name}.inhibitor`} />
                          <InputLabel
                            htmlFor={`${name}.inhibitor`}
                            required={isRequired('inhibitor')}
                          >
                            {t('inhibitor')}
                          </InputLabel>
                        </Stack>
                      )}
                      {show('ammonium') && (
                        <Stack direction="row" alignItems="center">
                          <CheckboxField name={`ammonium`} data-testid={`${name}.ammonium`} />
                          <InputLabel
                            htmlFor={`${name}.ammonium`}
                            required={isRequired('ammonium')}
                          >
                            {t('ammonium')}
                          </InputLabel>
                        </Stack>
                      )}
                    </Stack>
                  )}

                  {(show(FertilizerComponents.NITROGEN) ||
                    show(FertilizerComponents.PHOSPHORUS) ||
                    show(FertilizerComponents.POTASSIUM) ||
                    show(FertilizerComponents.SULFUR)) && (
                    <FormControl>
                      <Stack direction="row">
                        {FERTILIZER_COMPONENTS.filter(component => show(component)).map(
                          component => (
                            <FormLabel
                              key={`${name}.${component}`}
                              htmlFor={`${name}.${component}`}
                              sx={{ width: '25%' }}
                              required={isRequired(component)}
                            >
                              {t('fertilizerComponent', { context: component })}
                            </FormLabel>
                          )
                        )}
                      </Stack>
                      <Stack direction="row" sx={comboFieldStyle}>
                        {FERTILIZER_COMPONENTS.filter(component => show(component)).map(
                          component => (
                            <Field
                              key={`${name}.${component}`}
                              name={`${component}`}
                              component={TextField}
                              inputProps={{
                                min: 0,
                                max: 100,
                                type: 'number',
                                'data-testid': `${name}.${component}`,
                              }}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                              }}
                            />
                          )
                        )}
                      </Stack>

                      {!!combinedPercentError && (
                        <Typography color="error" variant="caption">
                          {combinedPercentError}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="caption">{t('requiredFields')}</Typography>
                    <Stack direction="row" justifyContent="flex-end" spacing={1}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          !field.initialValue && !field.touched && onPressRemove()
                          onEditEvent(false)
                          resetForm()
                          setExpanded(false)
                        }}
                        size="small"
                      >
                        {t('cancel')}
                      </Button>
                      <Button
                        data-testid="add-fertilizer-event"
                        variant="contained"
                        onClick={() => submitForm()}
                        disabled={!isValid || !dirty}
                        size="small"
                      >
                        {t(!!field.initialValue ? 'update' : 'add')}
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>
              </Collapse>
            </TableCell>
          </TableRow>
        )
      }}
    </Formik>
  )
}
